import {
  BarChart3,
  Briefcase,
  Building2,
  CalendarDays,
  ClipboardList,
  FileText,
  Wallet,
  HardHat,
  Home,
  Inbox,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { type ComponentType } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'

type AppModuleId =
  | 'dashboard'
  | 'properties'
  | 'deals-pipeline'
  | 'contacts'
  | 'documents'
  | 'tasks'
  | 'calendar'
  | 'notifications'
  | 'accounting'
  | 'leasing'
  | 'inbox'
  | 'automation'
  | 'lifecycle'
  | 'construction'
  | 'deal-analyzer'
  | 'ui-overhaul'

type ModuleBlock = {
  id: string
  title: string
  summary: string
  icon: ComponentType<{ className?: string }>
  status: 'core' | 'next'
  targetModule?: AppModuleId
}

const CORE_MODULES: ModuleBlock[] = [
  {
    id: 'deal-finder',
    title: 'Deal Finder',
    summary: 'Deal analysis, ARV, repair estimates, ROI and offer readiness in one flow.',
    icon: Search,
    status: 'core',
    targetModule: 'deal-analyzer',
  },
  {
    id: 'crm',
    title: 'Lead Management CRM',
    summary: 'Unified lead pipeline with follow-ups, reminders, notes and communication history.',
    icon: Briefcase,
    status: 'core',
    targetModule: 'contacts',
  },
  {
    id: 'property-workspace',
    title: 'Property Workspace',
    summary: 'A complete property record: timeline, photos, contracts, permits, payments and docs.',
    icon: Building2,
    status: 'core',
    targetModule: 'properties',
  },
  {
    id: 'construction',
    title: 'Construction Management',
    summary: 'Task boards, contractor schedules, budget-vs-actual, inspections and change orders.',
    icon: HardHat,
    status: 'core',
    targetModule: 'construction',
  },
  {
    id: 'financial-center',
    title: 'Financial Center',
    summary: 'Capital stack, draw schedules, expenses, receipts, cash flow and ROI visibility.',
    icon: Wallet,
    status: 'core',
    targetModule: 'accounting',
  },
  {
    id: 'rental-management',
    title: 'Rental Management',
    summary: 'Lease lifecycle, rent operations, maintenance requests and occupancy tracking.',
    icon: Home,
    status: 'core',
    targetModule: 'leasing',
  },
  {
    id: 'document-center',
    title: 'Document Center',
    summary: 'All contracts, invoices, reports and permits with search and history.',
    icon: FileText,
    status: 'core',
    targetModule: 'documents',
  },
  {
    id: 'communications',
    title: 'Unified Communications',
    summary: 'Email, SMS, notes and conversations tied to leads, properties and vendors.',
    icon: Inbox,
    status: 'core',
    targetModule: 'inbox',
  },
  {
    id: 'calendar',
    title: 'Calendar + Task Ops',
    summary: 'Appointments, inspections, closings, follow-ups and deadlines in one timeline.',
    icon: CalendarDays,
    status: 'core',
    targetModule: 'calendar',
  },
  {
    id: 'team-management',
    title: 'Team Management',
    summary: 'Role-based visibility, assignment ownership and accountability for growing teams.',
    icon: Users,
    status: 'next',
    targetModule: 'lifecycle',
  },
  {
    id: 'dashboard',
    title: 'Executive Dashboard',
    summary: 'Portfolio performance, pipeline health, budget variance and action queues.',
    icon: BarChart3,
    status: 'core',
    targetModule: 'dashboard',
  },
  {
    id: 'investor-reports',
    title: 'Investor Reports',
    summary: 'Monthly and quarterly reporting for equity, ROI, cash flow and portfolio value.',
    icon: ClipboardList,
    status: 'next',
    targetModule: 'notifications',
  },
]

const REMOVED_FOR_MIXED_OPERATOR = [
  'Tenant-only role switching and split persona dashboards',
  'Duplicated modules that fragment one property lifecycle into too many separate views',
  'Tool-specific UI dead ends not tied to lead -> deal -> rehab -> rental outcomes',
]

export default function InvestorOSModule({ onOpenModule }: { onOpenModule: (moduleId: AppModuleId) => void }) {
  return (
    <div className="space-y-4" data-testid="investor-os-hub">
      <Card className="border-none bg-gradient-to-r from-emerald-100 via-white to-cyan-100">
        <CardHeader>
          <Badge variant="success" className="w-fit">
            <ShieldCheck className="mr-1 h-3.5 w-3.5" />
            Investor OS v1.0
          </Badge>
          <CardTitle className="mt-2 text-2xl">Investor Operating System</CardTitle>
          <p className="text-sm text-slate-700">
            One-stop operating model for mixed operators handling acquisitions, rehab, rentals, finance and reporting.
          </p>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Core Modules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {CORE_MODULES.map((module) => {
              const Icon = module.icon
              return (
                <div key={module.id} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-emerald-600" />
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{module.title}</p>
                    </div>
                    <Badge variant={module.status === 'core' ? 'success' : 'info'}>{module.status === 'core' ? 'Core' : 'Next'}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{module.summary}</p>
                  {module.targetModule && (
                    <Button
                      className="mt-3 h-8"
                      size="sm"
                      variant={module.status === 'core' ? 'default' : 'secondary'}
                      onClick={() => onOpenModule(module.targetModule!)}
                    >
                      {module.status === 'core' ? 'Open Module' : 'Open Related Module'}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Removed To Keep It One-Stop</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {REMOVED_FOR_MIXED_OPERATOR.map((item) => (
            <div key={item} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
              {item}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
